import { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, Button, IconButton,
  InputAdornment, CircularProgress, Card, Divider, Stack, Table,
  TableBody, TableCell, TableHead, TableRow, Chip, useTheme, Alert
} from '@mui/material';
import {
  Edit, Save, Cancel, Calculate, TrendingUp, TrendingDown, CalendarMonth, Delete
} from '@mui/icons-material';
// Importa Recharts para os dois gráficos
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell // <-- Adicionado PieChart e Pie
} from 'recharts';
import {
  getDashboardSummary,
  updateCaixa,
  // getDashboardAnalytics, // Removido
  getDespesas,
  getPagamentos,
  deleteDespesa,
  deletePagamento,
  getContratos,
  listDespesasRecorrentes,
  getRestaurantes,
  getCategoriasDespesas,
} from '../services/api';
// import FluxoCaixaChart from './dashboard/FluxoCaixaChart'; // Substituído
// import DespesasCategoriaChart from './dashboard/DespesasCategoriaChart'; // SUBSTITUÍDO

/* ----------------- Helpers numéricos e datas ----------------- */
const pickNumericValue = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};
const toSafeNumber = (...values) => pickNumericValue(...values);

const parseUTCDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCHours(0,0,0,0);
  return d;
};
const formatDateKey = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth()+1).padStart(2,'0');
  const d = String(date.getUTCDate()).padStart(2,'0');
  return `${y}-${m}-${d}`;
};
const todayUTC = () => {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
};
const isSameMonthUTC = (dateString, y, m) => {
  if (!dateString) return false;
  const d = parseUTCDate(dateString); 
  if (!d) return false;
  return d.getUTCFullYear() === y && d.getUTCMonth() === m;
};
const currentYearMonthUTC = () => {
  const n = new Date();
  return { y: n.getUTCFullYear(), m: n.getUTCMonth() };
};
const clampDayToMonth = (year, monthIndex, day) => {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const safe = Math.min(Math.max(1, Number(day) || 1), lastDay);
  return new Date(Date.UTC(year, monthIndex, safe));
};
const addMonthsKeepingDay = (date, monthsToAdd, referenceDay) => {
  const y = date.getUTCFullYear();
  const m = date.getUTCMonth();
  const tgt = m + (Number.isFinite(monthsToAdd) ? monthsToAdd : 0);
  const tgtY = y + Math.floor(tgt/12);
  const tgtM = ((tgt%12)+12)%12;
  const day = Number.isFinite(referenceDay) ? referenceDay : date.getUTCDate();
  return clampDayToMonth(tgtY, tgtM, day);
};
/* ------------------ Helpers de rótulo/descrição ------------------ */
const pickFirstNonEmpty = (...values) => {
  for (const v of values) if (typeof v === 'string' && v.trim()) return v.trim();
  return null;
};
const buildContractLabel = (contrato, restauranteMap) => {
  if (!contrato) return 'Contrato';
  const direct = pickFirstNonEmpty(
    contrato?.restaurantes?.nome,
    contrato?.restaurante?.nome,
    contrato?.restaurante_nome,
    contrato?.cliente?.nome,
    contrato?.cliente_nome,
    contrato?.nome,
  );
  if (direct) return direct;
  if (restauranteMap && contrato?.restaurante_id && restauranteMap.has(contrato.restaurante_id)) {
    return restauranteMap.get(contrato.restaurante_id);
  }
  return contrato?.id ? `Contrato #${contrato.id}` : 'Contrato';
};
const buildPagamentoLabel = (p, contratoMap, restauranteMap) => {
  if (p?.descricao) return p.descricao;
  const c = p?.contrato || (p?.contrato_id && contratoMap?.get(p.contrato_id));
  if (c) return buildContractLabel(c, restauranteMap);
  const direct = pickFirstNonEmpty(p?.restaurante?.nome, p?.restaurante_nome, p?.cliente?.nome, p?.cliente_nome);
  if (direct) return direct;
  return `Receita #${p?.id ?? ''}`.trim();
};
const buildDescriptorKey = (descricao, valor, date) => {
  const norm = (descricao || '').toString().trim().toLowerCase();
  const cents = Math.round((Number(valor) || 0) * 100);
  return `${norm}|${formatDateKey(date)}|${cents}`;
};

/* -------------------- Status helpers (Competência) -------------------- */
const computeReceitaStatus = ({ data_vencimento, data_pagamento }) => {
  if (data_pagamento) return 'pago'; 
  const t = todayUTC();
  const venc = parseUTCDate(data_vencimento);
  if (!venc) return 'pendente';
  return venc.getTime() <= t.getTime() ? 'pago' : 'pendente';
};
const computeDespesaStatus = ({ data_vencimento, data_pagamento }) => {
  if (data_pagamento) return 'paga'; 
  const t = todayUTC();
  const venc = parseUTCDate(data_vencimento);
  if (!venc) return 'pendente';
  return venc.getTime() <= t.getTime() ? 'paga' : 'pendente';
};

// Lógica de Caixa (Pendentes): Apenas o que NÃO foi pago
const computeDespesaStatusPendente = ({ data_vencimento, data_pagamento }) => {
  if (data_pagamento) return 'paga';
  const t = todayUTC();
  const venc = parseUTCDate(data_vencimento);
  if (!venc) return 'pendente';
  return venc < t ? 'atrasada' : 'pendente';
};

// Cores para o gráfico de pizza
const PIE_COLORS = [
  '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#AF19FF', 
  '#FF19AF', '#19AFFF', '#FF5733', '#C70039', '#900C3F'
];


/* ---------------------- UI ---------------------- */
const StatCard = ({ title, value, color, children }) => (
  <Paper sx={{ p:2, borderRadius:2, height:'100%', display:'flex', flexDirection:'column' }}>
    <Typography variant="body2" color="text.secondary">{title}</Typography>
    <Typography variant="h5" fontWeight={700} color={color} sx={{ flexGrow:1 }}>
      {value.toLocaleString('pt-BR', { style:'currency', currency:'BRL' })}
    </Typography>
    {children}
  </Paper>
);

const META_MENSAL_CONTRATOS = 6.5;

const DashboardPage = () => {
  const theme = useTheme(); 
  const [summary, setSummary] = useState(null);
  const [isEditingCaixa, setIsEditingCaixa] = useState(false);
  const [caixaValue, setCaixaValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // fontes para KPI e projeção
  const [despesas, setDespesas] = useState([]);
  const [despesasRecorrentes, setDespesasRecorrentes] = useState([]);
  const [pagamentos, setPagamentos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [restaurantes, setRestaurantes] = useState([]);
  const [categorias, setCategorias] = useState([]); 

  // projeção
  const [projectionDate, setProjectionDate] = useState('');
  const [isLoadingProjectionData, setIsLoadingProjectionData] = useState(true);
  const [receitasAdicionais, setReceitasAdicionais] = useState('');
  const [despesasAdicionais, setDespesasAdicionais] = useState('');

  /* --------- load resumo/analytics --------- */
  const loadData = async () => {
    try {
      const [s] = await Promise.all([getDashboardSummary()]);
      setSummary(s);
      setCaixaValue(s.caixa_atual);
    } catch (e) {
      console.error('Erro ao buscar dashboard:', e);
    }
  };
  useEffect(() => { loadData(); }, []);

  /* --------- load fontes para projeção/KPI mensal --------- */
  const loadProjectionSources = useCallback(async () => {
    setIsLoadingProjectionData(true);
    try {
      const [desp, pags, cons, rests, cats] = await Promise.all([ 
        getDespesas().catch(()=>[]),
        getPagamentos().catch(()=>[]),
        getContratos().catch(()=>[]),
        getRestaurantes().catch(()=>[]),
        getCategoriasDespesas().catch(()=>[]), 
      ]);
      setDespesas(Array.isArray(desp) ? desp : []);
      setPagamentos(Array.isArray(pags) ? pags : []);
      setContratos(Array.isArray(cons) ? cons : []);
      setRestaurantes(Array.isArray(rests) ? rests : []);
      setCategorias(Array.isArray(cats) ? cats : []); 
      const rec = await listDespesasRecorrentes().catch(()=>[]);
      setDespesasRecorrentes(Array.isArray(rec) ? rec : []);
    } catch (e) {
      console.error('Erro ao buscar dados para projeção:', e);
    } finally {
      setIsLoadingProjectionData(false);
    }
  }, []);
  useEffect(() => { loadProjectionSources(); }, [loadProjectionSources]);

  const handleUpdateCaixa = async () => {
    setIsLoading(true);
    try {
      await updateCaixa(caixaValue);
      setIsEditingCaixa(false);
      await loadData();
    } catch {
      alert('Erro ao atualizar o valor do caixa.');
    } finally {
      setIsLoading(false);
    }
  };

  /* =========================
     KPI: A Pagar (MÊS ATUAL)
     ========================= */
  const { y: curY, m: curM } = currentYearMonthUTC();

  // *** CORREÇÃO: Junta todas as despesas (reais e templates) em uma só lista ***
  const allDespesas = useMemo(() => [
    ...despesas, 
    ...despesasRecorrentes
  ], [despesas, despesasRecorrentes]);

  const monthExistingDescriptorKeys = useMemo(() => {
    const keys = new Set();
    (despesas || []).forEach((d) => {
      if (!isSameMonthUTC(d?.data_vencimento, curY, curM)) return;
      const date = parseUTCDate(d?.data_vencimento) || parseUTCDate(d?.data_pagamento);
      if (!date) return;
      const val = Number(d?.valor || 0);
      const desc = (d?.descricao || `Despesa #${d?.id ?? ''}`).trim();
      keys.add(buildDescriptorKey(desc, val, date));
    });
    return keys;
  }, [despesas, curY, curM]);

  const monthBaseDespesasTotal = useMemo(() => {
    return (despesas || []).reduce((acc, d) => {
      const ref = d?.data_vencimento || d?.data_pagamento;
      if (!isSameMonthUTC(ref, curY, curM)) return acc;
      const st = computeDespesaStatusPendente(d); 
      if (!['pendente', 'atrasada'].includes(st)) return acc;
      const val = Number(d?.valor || 0);
      return acc + (Number.isNaN(val) ? 0 : val);
    }, 0);
  }, [despesas, curY, curM]);

  const monthRecorrentesTotal = useMemo(() => {
    let total = 0;
    (despesasRecorrentes || []).forEach((cfg) => {
      const status = (cfg?.ativo ?? cfg?.status ?? 'ativo').toString().trim().toLowerCase();
      if (['inativo', 'cancelado', 'desativado', 'encerrado'].includes(status)) return;

      const seedDate = parseUTCDate(cfg?.data_inicio ?? cfg?.inicio ?? cfg?.data_vencimento);
      const seedDay = Number(cfg?.dia_vencimento ?? cfg?.dia ?? (seedDate ? seedDate.getUTCDate() : NaN));
      if (!Number.isFinite(seedDay)) return;

      const start = seedDate || todayUTC();
      const end = parseUTCDate(cfg?.data_fim ?? cfg?.fim ?? cfg?.recorrencia_fim);

      const monthDate = clampDayToMonth(curY, curM, seedDay);
      if (monthDate.getUTCFullYear() < start.getUTCFullYear()
          || (monthDate.getUTCFullYear() === start.getUTCFullYear() && monthDate.getUTCMonth() < start.getUTCMonth())) {
        return;
      }
      if (end && monthDate.getTime() > end.getTime()) return;

      const val = toSafeNumber(cfg?.valor, cfg?.valor_base, cfg?.valor_estimado);
      if (!val) return;

      const desc = (cfg?.descricao || `Despesa recorrente #${cfg?.id ?? ''}`).trim();
      const key = buildDescriptorKey(desc, val, monthDate);
      if (monthExistingDescriptorKeys.has(key)) return; 

      total += val;
    });

    return total;
  }, [despesasRecorrentes, curY, curM, monthExistingDescriptorKeys]);

  const aPagarMesAtual = monthBaseDespesasTotal + monthRecorrentesTotal;
  
  // BALANÇO REMOVIDO

  /* =========================
     PROJEÇÃO POR DATA
     ========================= */
  const projectionTargetDate = useMemo(() => {
    if (!projectionDate) return null;
    const d = parseUTCDate(projectionDate);
    if (!d) return null;
    d.setUTCHours(23,59,59,999);
    return d;
  }, [projectionDate]);

  const restauranteMap = useMemo(() => {
    const map = new Map();
    (restaurantes || []).forEach((r) => {
      if (!r?.id) return;
      const name = pickFirstNonEmpty(r?.nome, r?.razao_social, r?.nome_fantasia, r?.apelido);
      if (name) map.set(r.id, name);
    });
    (contratos || []).forEach((c) => {
      if (!c?.restaurante_id) return;
      const name = pickFirstNonEmpty(c?.restaurante_nome, c?.restaurante?.nome, c?.restaurantes?.nome, c?.cliente?.nome, c?.cliente_nome);
      if (name && !map.has(c.restaurante_id)) map.set(c.restaurante_id, name);
    });
    return map;
  }, [restaurantes, contratos]);

  const contratoMap = useMemo(() => {
    const map = new Map();
    (contratos || []).forEach((c) => { if (c?.id) map.set(c.id, c); });
    return map;
  }, [contratos]);

  const contratosNovosMes = useMemo(() => {
    const invalidStatuses = new Set(['cancelado', 'cancelada', 'inativo', 'inativa', 'encerrado', 'finalizado']);
    return (contratos || []).reduce((acc, contrato) => {
      const status = (contrato?.status || '').toString().trim().toLowerCase();
      if (invalidStatuses.has(status)) return acc;
      const inicio = parseUTCDate(
        contrato?.data_inicio
        ?? contrato?.dataInicio
        ?? contrato?.created_at
        ?? contrato?.createdAt
      );
      if (!inicio) return acc;
      if (inicio.getUTCFullYear() !== curY || inicio.getUTCMonth() !== curM) return acc;
      return acc + 1;
    }, 0);
  }, [contratos, curY, curM]);

  const metaMensalRestante = Math.max(META_MENSAL_CONTRATOS - contratosNovosMes, 0);
  const metaMensalBatida = contratosNovosMes >= META_MENSAL_CONTRATOS;

  const projectionData = useMemo(() => {
    if (!projectionTargetDate) return { entries: [], receitasTotal: 0, despesasTotal: 0 };

    const receitasEntries = [];
    const despesasEntries = [];
    const existingReceitaKeys = new Set();
    const existingDespesaRecurringKeys = new Set();
    const existingDespesaDescriptorKeys = new Set();

    const normalizedTarget = projectionTargetDate;
    const normalizedToday = todayUTC(); 

    // RECEITAS reais
    (pagamentos || []).forEach((p) => {
      const due = parseUTCDate(p?.data_vencimento) || parseUTCDate(p?.data_pagamento);
      if (!due || due > normalizedTarget || due < normalizedToday) return;

      const val = p?.status === 'pago'
        ? toSafeNumber(p?.valor_pago, p?.valor_devido)
        : toSafeNumber(p?.valor_devido, p?.valor_pago);

      receitasEntries.push({
        id: p.id,
        tipo: 'receita',
        descricao: buildPagamentoLabel(p, contratoMap, restauranteMap),
        data: due,
        valor: Number.isNaN(val) ? 0 : val,
        status: p?.data_pagamento ? 'paga' : 'pendente',
        isSimulated: false,
      });

      const cid = p?.contrato_id || p?.contrato?.id;
      if (cid) existingReceitaKeys.add(`contrato-${cid}-${formatDateKey(due)}`);
    });

    // RECEITAS recorrentes simuladas
    const activeStatuses = new Set(['ativo', 'trial', 'inadimplente']);
    (contratos || []).forEach((c) => {
      if (!c?.id) return;
      const st = (c?.status || '').toString().trim().toLowerCase();
      if (st && !activeStatuses.has(st)) return;

      const day = Number(c?.dia_vencimento ?? c?.dia_pagamento ?? c?.dia_cobranca);
      if (!Number.isFinite(day)) return;

      const valor = toSafeNumber(c?.valor_mensal, c?.valor, c?.valor_devido, c?.valor_base);
      if (!valor) return;

      const ini = parseUTCDate(c?.data_inicio ?? c?.dataInicio);
      if (!ini) return; 
      const fim = parseUTCDate(c?.data_fim ?? c?.dataFim);

      let occ = clampDayToMonth(ini.getUTCFullYear(), ini.getUTCMonth(), day);
      if (occ.getTime() < ini.getTime()) {
        occ = addMonthsKeepingDay(occ, 1, day);
      }
      
      while (occ && occ.getTime() < normalizedToday.getTime()) {
        occ = addMonthsKeepingDay(occ, 1, day);
      }

      let i = 0;
      while (occ && occ <= normalizedTarget && i < 240) { 
        i += 1;
        if (fim && occ > fim) break; 
        
        const key = `contrato-${c.id}-${formatDateKey(occ)}`;
        if (!existingReceitaKeys.has(key)) {
          receitasEntries.push({
            id: `simulated-receita-${c.id}-${key}`,
            tipo: 'receita',
            descricao: buildContractLabel(c, restauranteMap),
            data: occ,
            valor,
            status: 'simulado',
            isSimulated: true,
          });
          existingReceitaKeys.add(key);
        }
        occ = addMonthsKeepingDay(occ, 1, day); 
      }
    });

    // DESPESAS reais (avulsas/parceladas)
    (despesas || []).forEach((d) => {
      if (!d || d?.status === 'cancelada') return;
      const due = parseUTCDate(d?.data_vencimento) || parseUTCDate(d?.data_pagamento);
      
      if (!due || due > normalizedTarget || due < normalizedToday) return;

      const val = toSafeNumber(d?.valor);
      const desc = (d?.descricao || `Despesa #${d?.id ?? ''}`).trim();

      despesasEntries.push({
        id: d.id,
        tipo: 'despesa',
        descricao: desc,
        data: due,
        valor: val,
        status: d?.data_pagamento ? 'paga' : 'pendente',
        isSimulated: false,
      });

      const recId = d?.despesa_recorrente_id ?? d?.recorrente_id ?? d?.financeiro_despesa_recorrente_id;
      if (recId) existingDespesaRecurringKeys.add(`rec-${recId}-${formatDateKey(due)}`);
      existingDespesaDescriptorKeys.add(buildDescriptorKey(desc, val, due));
    });

    // DESPESAS recorrentes simuladas
    (despesasRecorrentes || []).forEach((cfg) => {
      const status = (cfg?.ativo ?? cfg?.status ?? 'ativo').toString().trim().toLowerCase();
      if (['inativo','cancelado','desativado','encerrado'].includes(status)) return;

      const seed = parseUTCDate(cfg?.data_inicio ?? cfg?.inicio ?? cfg?.data_vencimento);
      if (!seed) return; 
      
      const day = Number(cfg?.dia_vencimento ?? cfg?.dia ?? (seed ? seed.getUTCDate() : NaN));
      if (!Number.isFinite(day)) return;

      const fim = parseUTCDate(cfg?.data_fim ?? cfg?.fim ?? cfg?.recorrencia_fim);
      const intervalo = Number(cfg?.intervalo_meses ?? cfg?.intervalo ?? 1) || 1;
      const valor = toSafeNumber(cfg?.valor, cfg?.valor_base, cfg?.valor_estimado);
      if (!valor) return;

      let occ = clampDayToMonth(seed.getUTCFullYear(), seed.getUTCMonth(), day);
      if (occ.getTime() < seed.getTime()) {
        occ = addMonthsKeepingDay(occ, intervalo, day);
      }

      while (occ && occ.getTime() < normalizedToday.getTime()) {
        occ = addMonthsKeepingDay(occ, intervalo, day);
      }

      let i = 0;
      while (occ && occ <= projectionTargetDate && i < 240) { 
        i += 1;
        if (fim && occ > fim) break;

        const recKey = `rec-${cfg.id}-${formatDateKey(occ)}`;
        const desc = (cfg?.descricao || `Despesa recorrente #${cfg?.id ?? ''}`).trim();
        const descrKey = buildDescriptorKey(desc, valor, occ);

        if (!existingDespesaRecurringKeys.has(recKey) && !existingDespesaDescriptorKeys.has(descrKey)) {
          despesasEntries.push({
            id: `simulated-despesa-${cfg.id}-${recKey}`,
            tipo: 'despesa',
            descricao: desc,
            data: occ,
            valor,
            status: 'simulada',
            isSimulated: true,
          });
          existingDespesaRecurringKeys.add(recKey);
          existingDespesaDescriptorKeys.add(descrKey);
        }

        occ = addMonthsKeepingDay(occ, intervalo, day); 
      }
    });

    const sorted = [...receitasEntries, ...despesasEntries].sort((a,b) => {
      if (!a.data && !b.data) return 0;
      if (!a.data) return 1;
      if (!b.data) return -1;
      return a.data.getTime() - b.data.getTime(); 
    });
    const receitasTotal = receitasEntries.reduce((s,e)=>s+(Number(e.valor)||0),0);
    const despesasTotal = despesasEntries.reduce((s,e)=>s+(Number(e.valor)||0),0);

    return { entries: sorted, receitasTotal, despesasTotal };
  }, [projectionTargetDate, pagamentos, despesas, contratos, despesasRecorrentes, contratoMap, restauranteMap]);

  const projectionTotals = useMemo(() => {
    if (!summary || !projectionTargetDate) return null;
    return {
      receitas: projectionData.receitasTotal,
      despesas: projectionData.despesasTotal,
      saldo: summary.caixa_atual + projectionData.receitasTotal - projectionData.despesasTotal,
    };
  }, [summary, projectionTargetDate, projectionData]);

  const projectionEntries = projectionData.entries;

  // ===== Hooks precisam vir antes de qualquer return =====
  const previsaoFinal = useMemo(() => {
    if (!summary) return 0;
    const base = summary.caixa_atual + summary.total_a_receber - aPagarMesAtual;
    const r = parseFloat(receitasAdicionais) || 0;
    const d = parseFloat(despesasAdicionais) || 0;
    return base + r - d;
  }, [summary, aPagarMesAtual, receitasAdicionais, despesasAdicionais]);

  /* ==================================================
     GRÁFICOS (CÁLCULO LOCAL - CORRIGIDO)
     ================================================== */
  
  // --- DADOS PARA O GRÁFICO DE FLUXO DE CAIXA (Regime de Competência) ---
  const fluxoCaixaMensalData = useMemo(() => {
    const data = [];
    const today = new Date();
    
    // ** CORREÇÃO **: Usa a lista 'allDespesas' que criamos lá em cima
    const allPagaveis = [
      ...(pagamentos || []).map(p => ({ item: p, tipo: 'receita' })),
      ...(allDespesas || []).map(d => ({ item: d, tipo: 'despesa' })) // <-- USA 'allDespesas'
    ];

    for (let i = 5; i >= 0; i--) { // Últimos 6 meses
      const targetDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - i, 1));
      const year = targetDate.getUTCFullYear();
      const month = targetDate.getUTCMonth();
      const mesLabel = targetDate.toLocaleString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', '');

      let receitas = 0;
      let despesas = 0;

      allPagaveis.forEach(entry => {
        const { item, tipo } = entry;
        // Usa a data de vencimento como referência
        const ref = item.data_vencimento || item.data_pagamento; 
        if (!ref || !isSameMonthUTC(ref, year, month)) {
          return;
        }

        const valor = toSafeNumber(item.valor, item.valor_devido, item.valor_pago);
        
        // =============================================================
        // *** 1. CORREÇÃO APLICADA AQUI ***
        // A lógica de status estava errada (usava 'pago' para ambos).
        // Agora, ela verifica 'pago' para receitas e 'paga' para despesas.
        // =============================================================
        if (tipo === 'receita') {
          if (computeReceitaStatus(item) === 'pago') {
            receitas += valor;
          }
        } else { // tipo === 'despesa'
          if (computeDespesaStatus(item) === 'paga') {
            despesas += valor;
          }
        }
      });
      
      data.push({ mes: mesLabel, receitas, despesas });
    }
    return data;
  }, [pagamentos, allDespesas]); // <-- CORREÇÃO: Depende de 'allDespesas'
  
  // --- DADOS PARA O GRÁFICO DE DESPESAS POR CATEGORIA (Regime de Competência) ---
  const categoriaMap = useMemo(() => new Map(categorias.map(c => [c.id, c.nome])), [categorias]);

  const despesasPorCategoriaData = useMemo(() => {
    const gastos = new Map();

    // ** CORREÇÃO **: Itera sobre 'allDespesas'
    allDespesas.forEach(d => {
      const ref = d.data_vencimento || d.data_pagamento;
      if (!ref || !isSameMonthUTC(ref, curY, curM) || !d.categoria_id) {
        return;
      }
      
      // Esta lógica já estava correta (usando 'paga')
      if (computeDespesaStatus(d) !== 'paga') return;
      
      const valor = toSafeNumber(d.valor);
      const catId = d.categoria_id;
      gastos.set(catId, (gastos.get(catId) || 0) + valor);
    });

    return Array.from(gastos.entries()).map(([id, valor]) => ({
      name: categoriaMap.get(id) || `ID ${id}`,
      value: valor,
    }));
  }, [allDespesas, categoriaMap, curY, curM]); // <-- CORREÇÃO: Depende de 'allDespesas'


  /* -------- Helpers não-hooks -------- */
  const formatDate = (date) => {
    if (!date) return '-';
    const d = typeof date === 'string' ? new Date(date) : date;
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
  };
  const formatCurrency = (v) => v.toLocaleString('pt-BR', { style:'currency', currency:'BRL' });

  const handleDeleteProjectionEntry = async (entry) => {
    if (entry?.isSimulated) {
      window.alert('Lançamentos simulados são gerados automaticamente e não podem ser removidos manualmente.');
      return;
    }
    const lbl = entry.tipo === 'receita' ? 'receita' : 'despesa';
    if (!window.confirm(`Deseja excluir esta ${lbl}?`)) return;
    try {
      if (entry.tipo === 'receita') await deletePagamento(entry.id);
      else await deleteDespesa(entry.id);
      await loadProjectionSources();
    } catch (e) {
      console.error('Erro ao excluir lançamento da projeção:', e);
      alert('Não foi possível excluir o lançamento. Tente novamente.');
    }
  };

  // ===== retorno condicional só DEPOIS de todos os hooks =====
  if (!summary) { 
    return (
      <Box sx={{display:'flex',justifyContent:'center',alignItems:'center',height:'100%'}}>
        <CircularProgress/>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>Dashboard Financeiro</Typography>
      <Grid container spacing={2}>
        {/* KPIs */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Caixa Atual" value={summary.caixa_atual} color="text.primary">
            {isEditingCaixa ? (
              <Box sx={{ mt:1, display:'flex', alignItems:'center', gap:1 }}>
                <TextField
                  type="number" size="small" value={caixaValue}
                  onChange={(e)=>setCaixaValue(e.target.value)}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                  variant="outlined" sx={{ maxWidth:150 }}
                />
                <IconButton color="success" onClick={handleUpdateCaixa} disabled={isLoading} aria-label="Salvar caixa">
                  {isLoading ? <CircularProgress size={20}/> : <Save/>}
                </IconButton>
                <IconButton color="error" onClick={()=>setIsEditingCaixa(false)} disabled={isLoading} aria-label="Cancelar edição do caixa">
                  <Cancel/>
                </IconButton>
              </Box>
            ) : (
              <Button size="small" startIcon={<Edit/>} onClick={()=>{ setCaixaValue(summary.caixa_atual); setIsEditingCaixa(true); }} sx={{ mt:1, p:0, alignSelf:'flex-start' }}>
                Editar Caixa
              </Button>
            )}
          </StatCard>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="A Receber (Pendente)" value={summary.total_a_receber} color="primary.main" />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="A Pagar (Pendente)" value={aPagarMesAtual} color="error.main" />
        </Grid>
        
        {/* CARD "BALANÇO DO MÊS" REMOVIDO */}

        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p:2, borderRadius:2, height:'100%', display:'flex', flexDirection:'column', gap:1 }}>
            <Typography variant="body2" color="text.secondary">Novos contratos (mês)</Typography>
            <Typography variant="h5" fontWeight={800} color={metaMensalBatida ? 'success.main' : 'text.primary'}>
              {contratosNovosMes}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Meta: {META_MENSAL_CONTRATOS} contratos
            </Typography>
            <Chip
              label={metaMensalBatida ? 'Meta batida' : `Restam ${metaMensalRestante.toFixed(1)} contratos`}
              size="small"
              color={metaMensalBatida ? 'success' : 'warning'}
              sx={{ alignSelf: 'flex-start' }}
            />
          </Paper>
        </Grid>

        {/* Projeção por data */}
        <Grid item xs={12}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <CalendarMonth color="primary" />
              <Typography variant="h6" fontWeight={700}>Projeção de Balanço por Data</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de referência"
                  value={projectionDate}
                  onChange={(e)=>setProjectionDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Selecione uma data para calcular a projeção (inclui receitas e TODAS as despesas **a partir de hoje** até a data).
                </Typography>
              </Grid>
              <Grid item xs={12} md={8}>
                {isLoadingProjectionData ? (
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <CircularProgress size={20} />
                    <Typography color="text.secondary">Carregando dados de projeção...</Typography>
                  </Box>
                ) : projectionDate && projectionTotals ? (
                  <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography color="text.secondary">Receitas previstas</Typography>
                        <Typography color="primary.main" variant="h6" fontWeight={700}>
                          {formatCurrency(projectionTotals.receitas)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography color="text.secondary">Despesas previstas</Typography>
                        <Typography color="error.main" variant="h6" fontWeight={700}>
                          {formatCurrency(projectionTotals.despesas)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography color="text.secondary">Saldo projetado</Typography>
                        <Typography variant="h5" fontWeight={800} color={projectionTotals.saldo >= 0 ? 'success.main' : 'error.main'}>
                          {formatCurrency(projectionTotals.saldo)}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Tipo</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell>Vencimento</TableCell>
                            <TableCell>Valor</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {projectionEntries.map((entry) => (
                            <TableRow key={`${entry.tipo}-${entry.id}`}>
                              <TableCell sx={{ width: 120 }}>
                                <Chip
                                  label={entry.tipo === 'receita' ? 'Receita' : 'Despesa'}
                                  color={entry.tipo === 'receita' ? 'primary' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>{entry.descricao}</TableCell>
                              <TableCell>{formatDate(entry.data)}</TableCell>
                              <TableCell>
                                <Typography color={entry.tipo === 'receita' ? 'primary.main' : 'error.main'} fontWeight={600}>
                                  {formatCurrency(entry.valor)}
                                </Typography>
                              </TableCell>
                              <TableCell>{entry.status}</TableCell>
                              <TableCell align="right">
                                {entry.isSimulated ? (
                                  <Typography variant="caption" color="text.secondary">Automático</Typography>
                                ) : (
                                  <IconButton color="error" size="small" onClick={() => handleDeleteProjectionEntry(entry)} aria-label="Excluir lançamento">
                                    <Delete fontSize="small" />
                                  </IconButton>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                          {projectionEntries.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                                Nenhum lançamento encontrado para a data selecionada.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Paper>
                  </Box>
                ) : (
                  <Typography color="text.secondary">Escolha uma data para ver a projeção do caixa.</Typography>
                )}
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Calculadora de previsão */}
        <Grid item xs={12}>
          <Card sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
              <Calculate color="primary"/>
              <Typography variant="h6" fontWeight={700}>Calculadora de Previsão de Caixa</Typography>
            </Stack>
            <Divider sx={{ mb: 2 }}/>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography color="text.secondary" gutterBottom>Valores base (automáticos):</Typography>
                <Typography>(+) {formatCurrency(summary.caixa_atual)} <Typography variant="caption" color="text.secondary">(Caixa Atual)</Typography></Typography>
                <Typography color="primary.main"> (+) {formatCurrency(summary.total_a_receber)} <Typography variant="caption" color="text.secondary">(A Receber)</Typography></Typography>
                <Typography color="error.main"> (-) {formatCurrency(aPagarMesAtual)} <Typography variant="caption" color="text.secondary">(A Pagar do Mês)</Typography></Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography color="text.secondary" gutterBottom>Adicione outras previsões:</Typography>
                <Stack spacing={2}>
                  <TextField
                    label="Outras Receitas Previstas" type="number" size="small"
                    value={receitasAdicionais} onChange={(e)=>setReceitasAdicionais(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TrendingUp color="success"/></InputAdornment> }}
                  />
                  <TextField
                    label="Outras Despesas Previstas" type="number" size="small"
                    value={despesasAdicionais} onChange={(e)=>setDespesasAdicionais(e.target.value)}
                    InputProps={{ startAdornment: <InputAdornment position="start"><TrendingDown color="error"/></InputAdornment> }}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign:{ xs:'left', md:'right' } }}>
                <Typography color="text.secondary">Resultado Previsto:</Typography>
                <Typography variant="h4" fontWeight={800} color={previsaoFinal >= 0 ? 'success.main' : 'error.main'}>
                  {formatCurrency(previsaoFinal)}
                </Typography>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* Gráficos (CORRIGIDOS) */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>Fluxo de Caixa (Últimos 6 Meses)</Typography>
            {fluxoCaixaMensalData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" maxHeight={300}>
                <BarChart data={fluxoCaixaMensalData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="mes" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="receitas" name="Receitas (Competência)" fill={theme.palette.primary.main} />
                  <Bar dataKey="despesas" name="Despesas (Competência)" fill={theme.palette.error.main} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Não há dados de pagamentos para exibir o fluxo de caixa.</Alert>
            )}
          </Paper>
        </Grid>

        {/* ============================================================= */}
        {/* *** 2. CORREÇÃO APLICADA AQUI *** */}
        {/* Substituí o componente <DespesasCategoriaChart> por um */}
        {/* PieChart (gráfico de pizza/donut) direto do Recharts. */}
        {/* Isso corrige o problema de layout e sobreposição. */}
        {/* ============================================================= */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: 400, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Despesas Pagas por Categoria (Mês Atual)
            </Typography>
            {despesasPorCategoriaData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={despesasPorCategoriaData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={60} // Raio interno (para fazer o "donut")
                    outerRadius={80} // Raio externo
                    fill="#8884d8"
                    paddingAngle={5}
                  >
                    {despesasPorCategoriaData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend 
                    layout="vertical" 
                    align="right" 
                    verticalAlign="middle"
                    iconType="circle"
                    // Estilos para evitar que a legenda sobreponha o gráfico ou quebre
                    wrapperStyle={{ 
                      paddingLeft: 16, 
                      maxHeight: '100%', 
                      overflowY: 'auto',
                    }}
                    // Formata o texto da legenda para quebrar a linha se for muito longo
                    formatter={(value, entry) => (
                      <span style={{ 
                        color: theme.palette.text.primary, 
                        wordWrap: 'break-word', 
                        display: 'inline-block', 
                        maxWidth: 150 // Ajuste a largura máxima conforme necessário
                      }}>
                        {value} ({formatCurrency(entry.payload.value)})
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <Alert severity="info">Não há despesas pagas neste mês para exibir.</Alert>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
